/**
 * ReviewPanel component.
 *
 * Panel for "Review & Reply" attention items (workflow_human_review,
 * agent_text_review). Surfaces the artefact the operator must judge —
 * the agent's drafted text — plus the context that prompted it, the
 * execution it came from, and a structured way to send a reply.
 *
 * Field-resolution strategy (Task: useful Responses panel)
 *
 * Different producers populate `details` differently, so the panel
 * tries known shapes in order:
 *
 *   1. `details.execution_summary` (workflow summariser output)
 *        - `.headline` is shown as a heading.
 *        - `.summary` is rendered as markdown.
 *   2. `details.pending_message` (agent_text_review — Python ADK agents)
 *        - Rendered as markdown.
 *   3. `details.proposed_resolution.body_text` (workflow proposed
 *      resolution body)
 *        - Rendered as markdown.
 *
 * When none of the above is present we show a real empty state with a
 * link to the underlying execution, instead of dumping the leftover
 * bookkeeping fields. See `getAgentOutput` below.
 *
 * Source / trigger context
 *
 *   - `details.trigger_payload` is rendered as a collapsible JSON
 *     preview when present (workflow items only).
 *   - `details.facets.{event_type, automation_name}` are surfaced as
 *     a one-line summary above the trigger block.
 *   - When the item is linked to an agent session, the
 *     `SessionContextSummary` chip is rendered so operators can pop
 *     out the full agent trace (mirrors ApprovalCardPanel).
 *
 * Operator actions
 *
 *   - "Use agent's draft" pre-fills the reply textarea with the agent
 *     text so operators can edit-and-send rather than rewrite. The
 *     existing backend contract only supports `resolution: 'reply'`
 *     with an optional `feedback` string for text reviews, so we do
 *     not pretend to offer separate approve/reject paths here.
 *   - "Reply & Resolve" submits whatever's in the textarea via the
 *     standard onResolve callback. "Dismiss" closes the panel.
 *
 * Raw details disclosure
 *
 *   - Collapsed by default. Reveals the full `details` JSON for
 *     engineers debugging a malformed item.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  ExternalLink,
  FileText,
  Inbox,
  MessageSquare,
  Send,
  Sparkles,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'
import { ChatMarkdown } from '@/shared/components/ChatMarkdown'
import type { AttentionItem } from '../types'
import {
  getAttentionTypeLabel,
  getAttentionTypeBadgeColor,
  getCustomerDisplay,
} from '../types'
import { SessionContextSummary } from './AgentSessionModal'

// ── Public Props ───────────────────────────────────────────────────────────

export interface ReviewPanelProps {
  /** The attention item being reviewed. */
  item: AttentionItem
  /** Whether a resolution action is pending. */
  actionPending?: boolean
  /** Called when the operator resolves with a reply. */
  onResolve?: (reply: string) => void
  /** Called when the operator dismisses the item. */
  onDismiss?: () => void
}

// ── Field Resolution ───────────────────────────────────────────────────────

/**
 * The agent's primary output, normalised across producers.
 *
 * `source` lets the UI show the operator where the text came from
 * (e.g. "From execution summariser" vs "Agent's drafted response"),
 * which is useful when the panel is showing a workflow summary
 * rather than a literal customer-facing draft.
 */
interface AgentOutput {
  headline: string | null
  text: string
  format: 'markdown' | 'plain'
  source:
    | 'pending_message'
    | 'execution_summary'
    | 'proposed_resolution'
    | 'subtitle'
}

/**
 * Resolve the agent's output from the `details` blob.
 *
 * Returns `null` when no recognised field carries any text. The
 * caller is expected to render an empty state in that case rather
 * than dumping raw JSON.
 */
function getAgentOutput(item: AttentionItem): AgentOutput | null {
  const details = (item.details ?? {}) as Record<string, unknown>

  // 1. Execution summariser output (workflow_human_review)
  const summary = details.execution_summary as
    | Record<string, unknown>
    | undefined
  if (summary && typeof summary === 'object') {
    const headline = typeof summary.headline === 'string' ? summary.headline : null
    const summaryText =
      typeof summary.summary === 'string' ? summary.summary : null
    if (headline || summaryText) {
      return {
        headline,
        text: summaryText ?? '',
        format: 'markdown',
        source: 'execution_summary',
      }
    }
  }

  // 2. Pending message (agent_text_review — primary ADK path)
  const pending = details.pending_message
  if (typeof pending === 'string' && pending.trim().length > 0) {
    return {
      headline: null,
      text: pending,
      format: 'markdown',
      source: 'pending_message',
    }
  }

  // 3. Proposed resolution body (workflow proposed resolutions)
  const proposed = details.proposed_resolution as
    | Record<string, unknown>
    | undefined
  if (proposed && typeof proposed === 'object') {
    const bodyText =
      typeof proposed.body_text === 'string' ? proposed.body_text : null
    if (bodyText && bodyText.trim().length > 0) {
      const title =
        typeof proposed.title === 'string' ? proposed.title : null
      return {
        headline: title,
        text: bodyText,
        format: 'markdown',
        source: 'proposed_resolution',
      }
    }
  }

  // 4. Subtitle fallback — the queue list often packs a 120-char
  // preview into the subtitle (see attention_service.go:342), so if
  // nothing else is available we surface that rather than nothing.
  if (item.subtitle && item.subtitle.trim().length > 0) {
    return {
      headline: null,
      text: item.subtitle,
      format: 'plain',
      source: 'subtitle',
    }
  }

  return null
}

/** Human label for an AgentOutput's source. */
function outputSourceLabel(source: AgentOutput['source']): string {
  switch (source) {
    case 'execution_summary':
      return "Summariser's report"
    case 'pending_message':
      return "Agent's drafted response"
    case 'proposed_resolution':
      return 'Proposed resolution'
    case 'subtitle':
      return 'Preview (truncated)'
  }
}

/**
 * Extract a short, plain-text summary of what triggered this
 * attention item. Used as a one-liner above the trigger payload.
 */
function getTriggerSummary(item: AttentionItem): string | null {
  const details = (item.details ?? {}) as Record<string, unknown>
  const facets = (details.facets ?? {}) as Record<string, unknown>

  const eventType =
    typeof details.event_type === 'string' ? details.event_type : null
  const automationName =
    typeof details.automation_name === 'string'
      ? details.automation_name
      : null
  const facetEventType =
    typeof facets.event_type === 'string' ? facets.event_type : null

  const parts: string[] = []
  if (automationName) parts.push(automationName)
  if (eventType || facetEventType) {
    parts.push(`event: ${eventType ?? facetEventType ?? ''}`)
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * The raw trigger payload, if present. We expose it as a collapsible
 * JSON view so the operator can see exactly what kicked the
 * automation off without having to open the execution detail page.
 */
function getTriggerPayload(item: AttentionItem): Record<string, unknown> | null {
  const details = (item.details ?? {}) as Record<string, unknown>
  const payload = details.trigger_payload
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as Record<string, unknown>
  }
  return null
}

/**
 * Compact step-execution summary for workflow items. Each entry
 * mirrors the shape produced by `executionToDetailsMap` in the Go
 * attention service.
 */
interface StepSummary {
  step_id: string
  step_type: string
  status: string
  error?: string
}

function getStepExecutions(item: AttentionItem): StepSummary[] {
  const details = (item.details ?? {}) as Record<string, unknown>
  const steps = details.step_executions
  if (!Array.isArray(steps)) return []
  const out: StepSummary[] = []
  for (const s of steps) {
    if (s && typeof s === 'object') {
      const obj = s as Record<string, unknown>
      out.push({
        step_id: typeof obj.step_id === 'string' ? obj.step_id : '',
        step_type: typeof obj.step_type === 'string' ? obj.step_type : '',
        status: typeof obj.status === 'string' ? obj.status : '',
        error: typeof obj.error === 'string' ? obj.error : undefined,
      })
    }
  }
  return out
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * Review panel for text review attention items.
 */
export function ReviewPanel({
  item,
  actionPending,
  onResolve,
  onDismiss,
}: ReviewPanelProps) {
  const [reply, setReply] = React.useState('')
  const [showTrigger, setShowTrigger] = React.useState(false)
  const [showSteps, setShowSteps] = React.useState(false)
  const [showRaw, setShowRaw] = React.useState(false)
  const [copied, setCopied] = React.useState(false)

  const badgeColor = getAttentionTypeBadgeColor(item.attention_type)
  const customer = getCustomerDisplay(item)
  const output = React.useMemo(() => getAgentOutput(item), [item])
  const triggerSummary = React.useMemo(() => getTriggerSummary(item), [item])
  const triggerPayload = React.useMemo(() => getTriggerPayload(item), [item])
  const steps = React.useMemo(() => getStepExecutions(item), [item])

  const handleSubmit = React.useCallback(() => {
    if (reply.trim() && onResolve) {
      onResolve(reply.trim())
    }
  }, [reply, onResolve])

  const handleUseDraft = React.useCallback(() => {
    if (output?.text) {
      setReply(output.text)
    }
  }, [output])

  const handleCopy = React.useCallback(async () => {
    if (!output?.text) return
    try {
      await navigator.clipboard.writeText(output.text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Best-effort: clipboard access can fail under sandboxing.
    }
  }, [output])

  // Build the execution deep-link — same convention as DebugReviewPanel.
  const executionHref = item.execution_id
    ? `/executions/${item.execution_id}/audit`
    : null

  return (
    <div style={styles.container} data-testid="review-panel">
      <Card>
        <CardHeader style={styles.header}>
          <div style={styles.headerRow}>
            <Badge
              variant="outline"
              style={{ borderColor: badgeColor, color: badgeColor }}
            >
              {getAttentionTypeLabel(item.attention_type)}
            </Badge>
            <CardTitle style={styles.title}>{item.title}</CardTitle>
          </div>
          <div style={styles.headerMeta}>
            {customer && <span style={styles.metaItem}>{customer}</span>}
            {item.agent_name && (
              <span style={styles.metaItem}>
                <span style={styles.metaLabel}>Agent:</span> {item.agent_name}
              </span>
            )}
            {executionHref && (
              <a
                href={executionHref}
                style={styles.executionLink}
                data-testid="open-execution-link"
              >
                <ExternalLink style={styles.linkIcon} />
                Open execution
              </a>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {/* Primary artefact — the agent's output */}
          {output ? (
            <div style={styles.outputContainer} data-testid="agent-output">
              <div style={styles.outputHeader}>
                <span style={styles.outputLabel}>
                  <Sparkles style={styles.outputLabelIcon} />
                  {outputSourceLabel(output.source)}
                </span>
                <div style={styles.outputActions}>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={handleCopy}
                    data-testid="copy-output-button"
                  >
                    {copied ? (
                      <>
                        <Check style={styles.iconButtonIcon} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy style={styles.iconButtonIcon} /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div style={styles.outputBody}>
                {output.headline && (
                  <h4 style={styles.outputHeadline}>{output.headline}</h4>
                )}
                {output.text && output.format === 'markdown' ? (
                  <ChatMarkdown
                    text={output.text}
                    density="compact"
                    data-testid="agent-output-markdown"
                  />
                ) : (
                  output.text && (
                    <p style={styles.outputPlain}>{output.text}</p>
                  )
                )}
              </div>
            </div>
          ) : (
            <div style={styles.emptyState} data-testid="empty-output-state">
              <Inbox style={styles.emptyIcon} />
              <div>
                <p style={styles.emptyTitle}>No drafted response in this item</p>
                <p style={styles.emptyBody}>
                  This agent didn't include a reviewable text payload.
                  {executionHref
                    ? ' Open the execution to see what it produced.'
                    : ' Check the agent session for context.'}
                </p>
              </div>
            </div>
          )}

          {/* Trigger / source context */}
          {(triggerSummary || triggerPayload) && (
            <div style={styles.section} data-testid="trigger-section">
              <button
                type="button"
                style={styles.sectionToggle}
                onClick={() => setShowTrigger((v) => !v)}
                aria-expanded={showTrigger}
              >
                {showTrigger ? (
                  <ChevronDown style={styles.toggleIcon} />
                ) : (
                  <ChevronRight style={styles.toggleIcon} />
                )}
                <FileText style={styles.sectionLabelIcon} />
                <span style={styles.sectionLabel}>What triggered this</span>
                {triggerSummary && !showTrigger && (
                  <span style={styles.sectionPreview}>{triggerSummary}</span>
                )}
              </button>
              {showTrigger && (
                <div style={styles.sectionBody}>
                  {triggerSummary && (
                    <p style={styles.triggerSummary}>{triggerSummary}</p>
                  )}
                  {triggerPayload && (
                    <pre style={styles.codeBlock}>
                      {JSON.stringify(triggerPayload, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Workflow step trace */}
          {steps.length > 0 && (
            <div style={styles.section} data-testid="steps-section">
              <button
                type="button"
                style={styles.sectionToggle}
                onClick={() => setShowSteps((v) => !v)}
                aria-expanded={showSteps}
              >
                {showSteps ? (
                  <ChevronDown style={styles.toggleIcon} />
                ) : (
                  <ChevronRight style={styles.toggleIcon} />
                )}
                <FileText style={styles.sectionLabelIcon} />
                <span style={styles.sectionLabel}>
                  Workflow trace ({steps.length} step{steps.length === 1 ? '' : 's'})
                </span>
              </button>
              {showSteps && (
                <div style={styles.sectionBody}>
                  <ol style={styles.stepList}>
                    {steps.map((s, idx) => (
                      <li key={`${s.step_id}-${idx}`} style={styles.stepRow}>
                        <span style={styles.stepId}>
                          {s.step_id || `step-${idx + 1}`}
                        </span>
                        {s.step_type && (
                          <span style={styles.stepType}>{s.step_type}</span>
                        )}
                        <span
                          style={{
                            ...styles.stepStatus,
                            color: stepStatusColor(s.status),
                          }}
                        >
                          {s.status || 'unknown'}
                        </span>
                        {s.error && (
                          <span style={styles.stepError}>{s.error}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* Session context (lifted from ApprovalCardPanel) */}
          {item.session_id && (
            <div style={styles.sessionWrap}>
              <SessionContextSummary sessionId={item.session_id} />
            </div>
          )}

          {/* Reply area */}
          <div style={styles.replySection}>
            <div style={styles.replyHeader}>
              <div style={styles.replyHeaderLeft}>
                <MessageSquare style={styles.replyIcon} />
                <span style={styles.replyLabel}>Your reply</span>
              </div>
              {output?.text && (
                <button
                  type="button"
                  style={styles.linkButton}
                  onClick={handleUseDraft}
                  disabled={actionPending}
                  data-testid="use-draft-button"
                >
                  Use agent's draft
                </button>
              )}
            </div>
            <textarea
              style={styles.textarea}
              placeholder="Send the agent's draft, edit it, or give feedback for next time..."
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={4}
              data-testid="reply-textarea"
            />
          </div>

          {/* Action buttons */}
          <div style={styles.actions}>
            <Button
              variant="default"
              disabled={!reply.trim() || actionPending}
              onClick={handleSubmit}
              data-testid="reply-resolve-button"
            >
              <Send style={styles.actionIcon} />
              {actionPending ? 'Sending...' : 'Reply & Resolve'}
            </Button>
            <Button
              variant="ghost"
              disabled={actionPending}
              onClick={onDismiss}
              data-testid="dismiss-button"
            >
              Dismiss
            </Button>
          </div>

          {/* Raw details — collapsed engineer disclosure */}
          {item.details && (
            <div style={styles.rawSection} data-testid="raw-section">
              <button
                type="button"
                style={styles.rawToggle}
                onClick={() => setShowRaw((v) => !v)}
                aria-expanded={showRaw}
                data-testid="raw-toggle"
              >
                {showRaw ? (
                  <ChevronDown style={styles.toggleIconSmall} />
                ) : (
                  <ChevronRight style={styles.toggleIconSmall} />
                )}
                {showRaw ? 'Hide raw details' : 'Show raw details'}
              </button>
              {showRaw && (
                <pre style={styles.codeBlock} data-testid="raw-details-pre">
                  {JSON.stringify(item.details, null, 2)}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────

function stepStatusColor(status: string): string {
  switch (status) {
    case 'succeeded':
    case 'completed':
    case 'ok':
      return 'var(--success, #22c55e)'
    case 'failed':
    case 'error':
      return 'var(--error, #ef4444)'
    case 'pending':
    case 'running':
      return 'var(--warning, #f59e0b)'
    default:
      return 'var(--text-muted)'
  }
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  metaLabel: {
    color: 'var(--text-muted)',
  },
  executionLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: 'var(--accent-primary)',
    textDecoration: 'none',
  },
  linkIcon: {
    width: '12px',
    height: '12px',
  },
  outputContainer: {
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-tertiary)',
    marginBottom: '16px',
    overflow: 'hidden',
  },
  outputHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'rgba(99, 102, 241, 0.05)',
  },
  outputLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: 'var(--accent-primary)',
  },
  outputLabelIcon: {
    width: '12px',
    height: '12px',
  },
  outputActions: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    transition: 'background-color 150ms, color 150ms',
  },
  iconButtonIcon: {
    width: '12px',
    height: '12px',
  },
  outputBody: {
    padding: '12px 14px',
    maxHeight: '420px',
    overflow: 'auto',
  },
  outputHeadline: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
    lineHeight: 1.4,
  },
  outputPlain: {
    fontSize: '14px',
    color: 'var(--text-primary)',
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
    lineHeight: 1.6,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    marginBottom: '16px',
    borderRadius: 'var(--radius-sm)',
    border: '1px dashed var(--border-subtle)',
    background: 'var(--bg-tertiary)',
  },
  emptyIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--text-muted)',
    flexShrink: 0,
    marginTop: '2px',
  },
  emptyTitle: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    margin: '0 0 4px 0',
  },
  emptyBody: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    margin: 0,
    lineHeight: 1.5,
  },
  section: {
    marginBottom: '12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    overflow: 'hidden',
  },
  sectionToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '8px 12px',
    background: 'var(--bg-tertiary)',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    textAlign: 'left' as const,
  },
  toggleIcon: {
    width: '14px',
    height: '14px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  toggleIconSmall: {
    width: '12px',
    height: '12px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  sectionLabelIcon: {
    width: '12px',
    height: '12px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  sectionLabel: {
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  sectionPreview: {
    color: 'var(--text-muted)',
    marginLeft: '6px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
  },
  sectionBody: {
    padding: '10px 12px',
    borderTop: '1px solid var(--border-subtle)',
    background: 'var(--bg-primary)',
  },
  triggerSummary: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    margin: '0 0 8px 0',
  },
  codeBlock: {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid var(--border-subtle)',
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-secondary)',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    margin: 0,
    overflow: 'auto',
    maxHeight: '260px',
  },
  stepList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    margin: 0,
    padding: 0,
    listStyle: 'none' as const,
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    flexWrap: 'wrap' as const,
  },
  stepId: {
    fontFamily: 'var(--font-mono, monospace)',
    color: 'var(--text-primary)',
    fontWeight: 500,
  },
  stepType: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    padding: '1px 6px',
    borderRadius: '3px',
    background: 'var(--bg-tertiary)',
  },
  stepStatus: {
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'capitalize' as const,
  },
  stepError: {
    fontSize: '11px',
    color: 'var(--error, #ef4444)',
    flexBasis: '100%',
    marginLeft: '0',
  },
  sessionWrap: {
    marginBottom: '16px',
  },
  replySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  replyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  replyHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  replyIcon: {
    width: '14px',
    height: '14px',
    color: 'var(--text-muted)',
  },
  replyLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-muted)',
  },
  linkButton: {
    fontSize: '11px',
    color: 'var(--accent-primary)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    fontWeight: 500,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    lineHeight: 1.5,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    outline: 'none',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  actionIcon: {
    width: '14px',
    height: '14px',
    marginRight: '6px',
  },
  rawSection: {
    marginTop: '8px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-subtle)',
  },
  rawToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
}

export default ReviewPanel
